import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Megaphone, Sparkles, Instagram, CalendarDays, Film, Image as ImageIcon,
  Copy, RefreshCw, Loader2, Hash, Clock, Lightbulb, ArrowLeft, Menu,
  Palette, ExternalLink, ChevronRight, Wand2, Download, Share2, BookOpen,
  AlertCircle, Settings
} from "lucide-react";
import { motion } from "framer-motion";
import type { Tables } from "@/integrations/supabase/types";
import { TrackingSlidesManager } from "@/components/menu/TrackingSlidesManager";

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
};

interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  category_name?: string;
}

interface GeneratedContent {
  caption: string;
  hashtags: string[];
  bestTime: string;
  tip: string;
}

interface CalendarDay {
  day: string;
  theme: string;
  caption: string;
  hashtags: string[];
  format: string;
  bestTime: string;
}

interface StoryIdea {
  title: string;
  type: "story" | "reel";
  description: string;
  script: string[];
  duration: string;
  tip: string;
}

const Marketing = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [restaurant, setRestaurant] = useState<Tables<"restaurants"> | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [tone, setTone] = useState<string>("divertido");
  const [generating, setGenerating] = useState(false);
  const [generatedCaption, setGeneratedCaption] = useState<GeneratedContent | null>(null);
  const [generatingCalendar, setGeneratingCalendar] = useState(false);
  const [calendar, setCalendar] = useState<CalendarDay[]>([]);
  const [generatingStories, setGeneratingStories] = useState(false);
  const [stories, setStories] = useState<StoryIdea[]>([]);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [imagePrompt, setImagePrompt] = useState("");
  const [canvaApiKey, setCanvaApiKey] = useState<string | null>(null);
  const [canvaLoading, setCanvaLoading] = useState(true);
  const [canvaDesignUrl, setCanvaDesignUrl] = useState<string | null>(null);
  const canvaScriptLoaded = useRef(false);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }

      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      if (impersonateId) {
        const { data } = await supabase.from("restaurants").select("*").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase.from("restaurants").select("*").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurant(rest);

      const { data: prods } = await supabase
        .from("products")
        .select("id, name, description, price, image_url, category_id")
        .eq("restaurant_id", rest.id)
        .eq("is_available", true)
        .order("name");

      // Get categories for context
      const { data: cats } = await supabase
        .from("categories")
        .select("id, name")
        .eq("restaurant_id", rest.id);

      const catMap = new Map((cats || []).map(c => [c.id, c.name]));
      setProducts((prods || []).map(p => ({
        ...p,
        category_name: p.category_id ? catMap.get(p.category_id) || undefined : undefined,
      })));
      setLoading(false);
    };
    load();
  }, [navigate]);

  // Load Canva API key from platform settings
  useEffect(() => {
    const loadCanvaConfig = async () => {
      setCanvaLoading(true);
      try {
        // canva_api_key is now stored in platform_secrets (admin-only)
        const { data } = await supabase
          .from("platform_settings")
          .select("value")
          .eq("key", "platform_secrets")
          .maybeSingle();
        
        const secrets = data?.value as any;
        if (secrets?.canva_api_key) {
          setCanvaApiKey(secrets.canva_api_key);
        }
      } catch (err) {
        console.error("Error loading canva config:", err);
      } finally {
        setCanvaLoading(false);
      }
    };
    loadCanvaConfig();
  }, []);

  // Load Canva Button SDK
  useEffect(() => {
    if (!canvaApiKey || canvaScriptLoaded.current) return;
    
    const script = document.createElement("script");
    script.src = "https://sdk.canva.com/designbutton/v2/api.js";
    script.async = true;
    script.onload = () => {
      canvaScriptLoaded.current = true;
    };
    document.body.appendChild(script);
    
    return () => {
      // Don't remove script on cleanup to avoid reload issues
    };
  }, [canvaApiKey]);

  const handleCanvaCreateDesign = useCallback(async (designType: string) => {
    if (!canvaApiKey) return;

    const w = window as any;
    if (!w.Canva || !w.Canva.DesignButton) {
      toast({ title: "Canva SDK ainda carregando", description: "Aguarde um momento e tente novamente.", variant: "destructive" });
      return;
    }

    try {
      const api = await w.Canva.DesignButton.initialize({ apiKey: canvaApiKey });
      
      const designTypeMap: Record<string, { width: number; height: number }> = {
        instagram_post: { width: 1080, height: 1080 },
        instagram_story: { width: 1080, height: 1920 },
        menu: { width: 1080, height: 1920 },
        banner: { width: 1200, height: 628 },
        logo: { width: 500, height: 500 },
        flyer: { width: 1080, height: 1350 },
      };

      const dimensions = designTypeMap[designType] || { width: 1080, height: 1080 };

      api.createDesign({
        design: {
          type: "custom",
          dimensions: { width: dimensions.width, height: dimensions.height, units: "px" },
        },
        onDesignPublish: (opts: { exportUrl: string; designId: string }) => {
          setCanvaDesignUrl(opts.exportUrl);
          toast({ title: "Design publicado!", description: "Sua arte foi criada com sucesso no Canva." });
        },
        onDesignClose: () => {
          // User closed the Canva editor
        },
      });
    } catch (err: any) {
      console.error("Canva error:", err);
      toast({ title: "Erro ao abrir Canva", description: err.message || "Não foi possível inicializar o editor.", variant: "destructive" });
    }
  }, [canvaApiKey, toast]);

  const handleGenerateCaption = async () => {
    if (!selectedProduct || !restaurant) return;
    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;

    setGenerating(true);
    setGeneratedCaption(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-marketing-content", {
        body: {
          type: "caption",
          product: { name: product.name, description: product.description, price: product.price, category: product.category_name },
          restaurant: { name: restaurant.name, type: restaurant.type },
          tone,
        },
      });
      if (error) throw error;
      setGeneratedCaption(data);
    } catch (err: any) {
      toast({ title: "Erro ao gerar legenda", description: err.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateCalendar = async () => {
    if (!restaurant) return;
    setGeneratingCalendar(true);
    setCalendar([]);
    try {
      const productContext = products.slice(0, 10).map(p => ({
        name: p.name, description: p.description, price: p.price, category: p.category_name,
      }));
      const { data, error } = await supabase.functions.invoke("generate-marketing-content", {
        body: {
          type: "calendar",
          products: productContext,
          restaurant: { name: restaurant.name, type: restaurant.type },
          tone,
        },
      });
      if (error) throw error;
      setCalendar(data?.days || []);
    } catch (err: any) {
      toast({ title: "Erro ao gerar calendário", description: err.message, variant: "destructive" });
    } finally {
      setGeneratingCalendar(false);
    }
  };

  const handleGenerateStories = async () => {
    if (!restaurant) return;
    setGeneratingStories(true);
    setStories([]);
    try {
      const productContext = products.slice(0, 8).map(p => ({
        name: p.name, description: p.description, price: p.price, category: p.category_name,
      }));
      const { data, error } = await supabase.functions.invoke("generate-marketing-content", {
        body: {
          type: "stories",
          products: productContext,
          restaurant: { name: restaurant.name, type: restaurant.type },
          tone,
        },
      });
      if (error) throw error;
      setStories(data?.ideas || []);
    } catch (err: any) {
      toast({ title: "Erro ao gerar ideias", description: err.message, variant: "destructive" });
    } finally {
      setGeneratingStories(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) return;
    setGeneratingImage(true);
    setGeneratedImage(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-marketing-content", {
        body: {
          type: "image",
          prompt: imagePrompt,
          restaurant: { name: restaurant?.name, type: restaurant?.type },
        },
      });
      if (error) throw error;
      if (data?.imageUrl) {
        setGeneratedImage(data.imageUrl);
      } else {
        throw new Error("Não foi possível gerar a imagem");
      }
    } catch (err: any) {
      toast({ title: "Erro ao gerar imagem", description: err.message, variant: "destructive" });
    } finally {
      setGeneratingImage(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copiado!", description: "Texto copiado para a área de transferência." });
  };

  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center shadow-lg">
              <Megaphone className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-display font-bold text-lg">Marketing</h1>
              <p className="text-xs text-muted-foreground">Crie conteúdo para suas redes sociais</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs defaultValue="captions" className="space-y-6">
          <TabsList className="grid grid-cols-2 md:grid-cols-6 gap-1 h-auto p-1 bg-secondary/50">
            <TabsTrigger value="captions" className="gap-2 text-xs md:text-sm py-2.5">
              <Instagram className="h-4 w-4" /> Legendas
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2 text-xs md:text-sm py-2.5">
              <CalendarDays className="h-4 w-4" /> Calendário
            </TabsTrigger>
            <TabsTrigger value="stories" className="gap-2 text-xs md:text-sm py-2.5">
              <Film className="h-4 w-4" /> Stories & Reels
            </TabsTrigger>
            <TabsTrigger value="image" className="gap-2 text-xs md:text-sm py-2.5">
              <ImageIcon className="h-4 w-4" /> Criar Imagem
            </TabsTrigger>
            <TabsTrigger value="canva" className="gap-2 text-xs md:text-sm py-2.5">
              <Palette className="h-4 w-4" /> Canva
            </TabsTrigger>
            <TabsTrigger value="tracking_slides" className="gap-2 text-xs md:text-sm py-2.5">
              <ExternalLink className="h-4 w-4" /> Slides Pedido
            </TabsTrigger>
          </TabsList>

          {/* === LEGENDAS === */}
          <TabsContent value="captions">
            <motion.div {...fadeUp} className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Gerar Legenda para Instagram
                  </CardTitle>
                  <CardDescription>Selecione um produto e o tom da postagem</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Produto</label>
                    <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um produto..." />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} - R$ {p.price.toFixed(2).replace(".", ",")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Tom da postagem</label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="divertido">🎉 Divertido</SelectItem>
                        <SelectItem value="profissional">💼 Profissional</SelectItem>
                        <SelectItem value="urgente">🔥 Urgente / Promoção</SelectItem>
                        <SelectItem value="elegante">✨ Elegante</SelectItem>
                        <SelectItem value="descontraido">😎 Descontraído</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleGenerateCaption}
                    disabled={!selectedProduct || generating}
                    className="w-full gap-2"
                  >
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                    {generating ? "Gerando..." : "Gerar Legenda"}
                  </Button>
                </CardContent>
              </Card>

              {generatedCaption && (
                <motion.div {...fadeUp}>
                  <Card className="border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Instagram className="h-5 w-5 text-pink-500" />
                          Legenda Gerada
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(generatedCaption.caption + "\n\n" + generatedCaption.hashtags.map(h => `#${h}`).join(" "))}>
                          <Copy className="h-4 w-4 mr-1" /> Copiar
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="bg-secondary/50 rounded-xl p-4 text-sm whitespace-pre-line">
                        {generatedCaption.caption}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {generatedCaption.hashtags.map((h, i) => (
                          <Badge key={i} variant="secondary" className="text-xs cursor-pointer hover:bg-primary/20" onClick={() => copyToClipboard(`#${h}`)}>
                            <Hash className="h-3 w-3 mr-0.5" />
                            {h}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Melhor horário: {generatedCaption.bestTime}
                        </span>
                      </div>
                      <div className="bg-primary/5 rounded-lg p-3 flex items-start gap-2">
                        <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground">{generatedCaption.tip}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </motion.div>
          </TabsContent>

          {/* === CALENDÁRIO === */}
          <TabsContent value="calendar">
            <motion.div {...fadeUp} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarDays className="h-5 w-5 text-primary" />
                    Calendário de Conteúdo Semanal
                  </CardTitle>
                  <CardDescription>Gere um plano de postagens para a semana baseado no seu cardápio</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleGenerateCalendar} disabled={generatingCalendar} className="gap-2">
                    {generatingCalendar ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                    {generatingCalendar ? "Gerando calendário..." : "Gerar Calendário Semanal"}
                  </Button>
                </CardContent>
              </Card>

              {calendar.length > 0 && (
                <div className="grid gap-4">
                  {calendar.map((day, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                      <Card className="hover:border-primary/30 transition-colors">
                        <CardContent className="p-4 md:p-5">
                          <div className="flex flex-col md:flex-row md:items-start gap-4">
                            <div className="flex items-center gap-3 md:w-40 shrink-0">
                              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                <CalendarDays className="h-5 w-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-semibold text-sm">{day.day}</p>
                                <Badge variant="outline" className="text-[10px] mt-1">{day.format}</Badge>
                              </div>
                            </div>
                            <div className="flex-1 space-y-2">
                              <p className="font-medium text-sm">{day.theme}</p>
                              <p className="text-sm text-muted-foreground whitespace-pre-line">{day.caption}</p>
                              <div className="flex flex-wrap gap-1">
                                {day.hashtags.map((h, j) => (
                                  <Badge key={j} variant="secondary" className="text-[10px]">#{h}</Badge>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" /> {day.bestTime}
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" className="shrink-0" onClick={() => copyToClipboard(day.caption + "\n\n" + day.hashtags.map(h => `#${h}`).join(" "))}>
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* === STORIES & REELS === */}
          <TabsContent value="stories">
            <motion.div {...fadeUp} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Film className="h-5 w-5 text-primary" />
                    Ideias de Stories e Reels
                  </CardTitle>
                  <CardDescription>Roteiros e ideias baseados nos produtos do seu cardápio</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleGenerateStories} disabled={generatingStories} className="gap-2">
                    {generatingStories ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                    {generatingStories ? "Gerando ideias..." : "Gerar Ideias de Stories & Reels"}
                  </Button>
                </CardContent>
              </Card>

              {stories.length > 0 && (
                <div className="grid md:grid-cols-2 gap-4">
                  {stories.map((story, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                      <Card className="h-full hover:border-primary/30 transition-colors">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base">{story.title}</CardTitle>
                            <Badge variant={story.type === "reel" ? "default" : "secondary"}>
                              {story.type === "reel" ? "🎬 Reel" : "📱 Story"}
                            </Badge>
                          </div>
                          <CardDescription>{story.description}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Roteiro</p>
                            {story.script.map((step, j) => (
                              <div key={j} className="flex items-start gap-2 text-sm">
                                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{j + 1}</span>
                                <span>{step}</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-border/50">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{story.duration}</span>
                            <span className="flex items-center gap-1"><Lightbulb className="h-3 w-3" />{story.tip}</span>
                          </div>
                          <Button variant="ghost" size="sm" className="w-full" onClick={() => copyToClipboard(story.script.join("\n"))}>
                            <Copy className="h-4 w-4 mr-1" /> Copiar Roteiro
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* === CRIAR IMAGEM COM IA === */}
          <TabsContent value="image">
            <motion.div {...fadeUp} className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5 text-primary" />
                    Gerar Imagem com IA
                  </CardTitle>
                  <CardDescription>Descreva a imagem que deseja criar para suas redes sociais</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="Ex: Uma foto apetitosa de um hambúrguer artesanal com queijo derretido, fundo escuro, estilo food photography profissional..."
                    className="min-h-[120px]"
                  />
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Promoção de almoço executivo",
                      "Post de novo prato no cardápio",
                      "Story de bastidores da cozinha",
                      "Banner de delivery",
                    ].map(suggestion => (
                      <Badge
                        key={suggestion}
                        variant="outline"
                        className="cursor-pointer hover:bg-primary/10 transition-colors text-xs"
                        onClick={() => setImagePrompt(suggestion)}
                      >
                        {suggestion}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    onClick={handleGenerateImage}
                    disabled={!imagePrompt.trim() || generatingImage}
                    className="w-full gap-2"
                  >
                    {generatingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                    {generatingImage ? "Gerando imagem..." : "Gerar Imagem"}
                  </Button>
                </CardContent>
              </Card>

              {generatedImage && (
                <motion.div {...fadeUp}>
                  <Card className="border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-base">Imagem Gerada</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="rounded-xl overflow-hidden border border-border">
                        <img src={generatedImage} alt="Imagem gerada" className="w-full h-auto" />
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" className="flex-1 gap-2" asChild>
                          <a href={generatedImage} download="marketing-image.png">
                            <Download className="h-4 w-4" /> Baixar
                          </a>
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => copyToClipboard(generatedImage)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </motion.div>
          </TabsContent>

          {/* === CANVA EMBARCADO === */}
          <TabsContent value="canva">
            <motion.div {...fadeUp}>
              {canvaLoading ? (
                <Card>
                  <CardContent className="py-12 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </CardContent>
                </Card>
              ) : !canvaApiKey ? (
                <Card className="border-destructive/30">
                  <CardContent className="py-10 flex flex-col items-center text-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
                      <AlertCircle className="h-7 w-7 text-destructive" />
                    </div>
                    <div>
                      <p className="font-semibold">Canva API Key não configurada</p>
                      <p className="text-sm text-muted-foreground mt-1 max-w-md">
                        Para usar o editor embarcado do Canva, é necessário configurar a API Key nas configurações da plataforma (Super Admin → Plataforma → Integrações).
                      </p>
                    </div>
                    <Button variant="outline" className="gap-2" onClick={() => navigate("/admin?tab=platform")}>
                      <Settings className="h-4 w-4" /> Ir para Configurações
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Palette className="h-5 w-5 text-primary" />
                      Editor de Imagem — Canva
                    </CardTitle>
                    <CardDescription>
                      Clique em um formato para abrir o editor do Canva diretamente na plataforma
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[
                        { title: "Post para Instagram", desc: "1080×1080px — formato quadrado ideal para o feed", type: "instagram_post", icon: Instagram },
                        { title: "Story do Instagram", desc: "1080×1920px — formato vertical para stories", type: "instagram_story", icon: Film },
                        { title: "Cardápio Digital", desc: "1080×1920px — template de cardápio", type: "menu", icon: BookOpen },
                        { title: "Banner de Promoção", desc: "1200×628px — banners para redes e delivery", type: "banner", icon: Megaphone },
                        { title: "Logo", desc: "500×500px — crie ou atualize seu logo", type: "logo", icon: Palette },
                        { title: "Flyer / Panfleto", desc: "1080×1350px — material para divulgação", type: "flyer", icon: Share2 },
                      ].map((item, i) => (
                        <Card
                          key={i}
                          className="cursor-pointer hover:border-primary/40 hover:shadow-lg transition-all group"
                          onClick={() => handleCanvaCreateDesign(item.type)}
                        >
                          <CardContent className="p-4 flex flex-col gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                              <item.icon className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-sm group-hover:text-primary transition-colors flex items-center gap-1">
                                {item.title}
                                <Wand2 className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {canvaDesignUrl && (
                      <motion.div {...fadeUp}>
                        <Card className="border-primary/20">
                          <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                              <Palette className="h-5 w-5 text-primary" />
                              Último Design Criado
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div className="rounded-xl overflow-hidden border border-border">
                              <img src={canvaDesignUrl} alt="Design do Canva" className="w-full h-auto" />
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" className="flex-1 gap-2" asChild>
                                <a href={canvaDesignUrl} download="canva-design.png">
                                  <Download className="h-4 w-4" /> Baixar
                                </a>
                              </Button>
                              <Button variant="outline" size="icon" onClick={() => copyToClipboard(canvaDesignUrl)}>
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    )}

                    <div className="bg-primary/5 rounded-xl p-4 flex items-start gap-3">
                      <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                      <p className="text-xs text-muted-foreground">
                        O editor do Canva será aberto em uma janela integrada. Ao finalizar, clique em "Publicar" no Canva para exportar a imagem de volta para a plataforma.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </TabsContent>

          {/* === TRACKING SLIDES === */}
          <TabsContent value="tracking_slides">
            <TrackingSlidesManager restaurantId={restaurant?.id || ""} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Marketing;
